import { Button } from '@pypestream/design-system';
import { observer } from 'mobx-react-lite';
import { FC, useEffect, useState } from 'react';

import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { tableStore } from '../../stores/table-store';
import type { AgentKey } from './agent-settings-model';
import { agentModelNodes, langfuseRefs, withRenamedRef } from './agent-prompt';
import {
  type PromptScope,
  type PromptView,
  copyPrompt,
  readPrompts,
  savePromptText,
} from './prompts-api';
import { saveParameterInput } from './use-agent-settings';

/**
 * The Langfuse prompts an agent's model calls name as `{langfuse.<name>}`
 * (the AI-Native template), each read from Langfuse.
 *
 * A shared prompt — every AI-Native solution reads it, live clients' too —
 * is shown read-only, with "Make a copy for this solution": that saves this
 * solution's own copy in Langfuse and points the nodes at it. A copy can be
 * edited, and each save is a new production version the AI Gateway uses from
 * the next message on.
 */
export const LangfusePromptCards: FC<{
  agent: AgentKey;
  isViewer: boolean;
  /** Other unsaved edits in the panel: a copy rewrites the node under them. */
  busy: boolean;
}> = observer(({ agent, isViewer, busy }) => {
  const scope = useProStudioCtxSelector((ctx) =>
    ctx.project?.id && ctx.customerId && ctx.botName
      ? {
          projectId: ctx.project.id,
          solutionKey: `${ctx.customerId}.${ctx.botName}`,
        }
      : null
  );
  const nodes = agentModelNodes(tableStore.rows, agent);
  const names = [
    ...new Set(nodes.flatMap((n) => langfuseRefs(n.parameterInput))),
  ];
  const namesKey = names.join('\n');

  const [prompts, setPrompts] = useState<PromptView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [working, setWorking] = useState<string | null>(null);

  const scopeKey = scope ? `${scope.projectId}|${scope.solutionKey}` : '';
  useEffect(() => {
    if (!scope || !names.length) {
      setPrompts([]);
      return undefined;
    }
    let live = true;
    setPrompts(null);
    setError(null);
    readPrompts(scope, names)
      .then((list) => live && setPrompts(list))
      .catch((err: Error) => live && setError(err.message));
    return () => {
      live = false;
    };
    // `names` and `scope` are keyed by their text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namesKey, scopeKey]);

  if (!names.length) return null;

  const replace = (from: string, next: PromptView) =>
    setPrompts((list) => {
      const others = (list ?? []).filter(
        (p) => p.name !== from && p.name !== next.name
      );
      const at = (list ?? []).findIndex((p) => p.name === from);
      return at < 0
        ? [...others, next]
        : [...others.slice(0, at), next, ...others.slice(at)];
    });

  const copy = async (s: PromptScope, shared: PromptView) => {
    setWorking(shared.name);
    setError(null);
    try {
      const own = await copyPrompt(s, shared.name);
      nodes.forEach((node) => {
        const next = withRenamedRef(node.parameterInput, shared.name, own.name);
        if (next !== null) saveParameterInput(node.id, next);
      });
      replace(shared.name, own);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(null);
    }
  };

  const save = async (s: PromptScope, own: PromptView, text: string) => {
    setWorking(own.name);
    setError(null);
    try {
      const saved = await savePromptText(s, { name: own.name, prompt: text });
      replace(own.name, saved);
      setEdits(({ [own.name]: _, ...rest }) => rest);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setWorking(null);
    }
  };

  return (
    <div
      className="c-agent-settings__lf"
      data-testid="bobcat-agent-prompts-langfuse"
    >
      <p className="c-agent-settings__sec-sub">
        Langfuse prompts it uses. The AI Gateway fills each in with its
        production version when the step runs.
      </p>
      {error && <p className="c-agent-settings__lf-error">{error}</p>}
      {prompts === null && !error && (
        <p className="c-agent-settings__none">Reading them from Langfuse…</p>
      )}
      {(prompts ?? []).map((p) => {
        const text = edits[p.name] ?? p.prompt ?? '';
        const changed =
          p.editable &&
          edits[p.name] !== undefined &&
          edits[p.name] !== p.prompt;
        return (
          <details
            key={p.name}
            className="c-agent-settings__lf-card"
            open={p.editable}
          >
            <summary>
              <b>{p.name}</b>
              <span>
                {p.shared
                  ? 'Shared'
                  : p.editable
                    ? 'This solution'
                    : 'Not editable here'}
                {p.version ? ` · v${p.version}` : ''}
                {p.labels?.includes('production') ? ' · production' : ''}
              </span>
            </summary>
            {p.unavailable ? (
              <p className="c-agent-settings__none">{p.unavailable}</p>
            ) : p.editable && !isViewer ? (
              <>
                <textarea
                  className="c-agent-settings__prompt"
                  value={text}
                  rows={10}
                  onChange={(e) =>
                    setEdits((m) => ({ ...m, [p.name]: e.target.value }))
                  }
                />
                <div className="c-agent-settings__lf-actions">
                  <Button
                    size="small"
                    variant="ghost"
                    disabled={!changed || working !== null}
                    onClick={() => setEdits(({ [p.name]: _, ...rest }) => rest)}
                  >
                    Discard
                  </Button>
                  <Button
                    size="small"
                    disabled={!changed || working !== null || !scope}
                    onClick={() => scope && void save(scope, p, text)}
                  >
                    {working === p.name
                      ? 'Saving…'
                      : `Save as v${(p.version ?? 0) + 1}`}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <pre className="c-agent-settings__lf-text">{text}</pre>
                {p.shared && !isViewer && (
                  <div className="c-agent-settings__lf-actions">
                    <span>
                      Every AI-Native solution uses this one. Copy it to change
                      it for this solution only.
                    </span>
                    <Button
                      size="small"
                      variant="secondary"
                      disabled={busy || working !== null || !scope}
                      onClick={() => scope && void copy(scope, p)}
                    >
                      {working === p.name
                        ? 'Copying…'
                        : 'Make a copy for this solution'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </details>
        );
      })}
    </div>
  );
});
